import { plainText } from './seo'
import type { Tutorial } from './types'

/**
 * Tutorial metadata from v1 PR #5: YouTube chapters (video_chapters) and the SEO enrichment blocks written by
 * `tutorials:seo-enrichment` (ai_summary, seo_description, what_youll_learn, ai_faq). Content may arrive as an
 * object or a JSON string.
 */

export interface Chapter {
  name: string
  startOffset: number
  position: number
}

export interface FaqItem {
  question: string
  answer: string
}

type WithMetadata = Pick<Tutorial, 'metadata'>

const parse = (value: unknown): unknown => {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

function contentOf(tutorial: WithMetadata, identifier: string): Record<string, unknown> | null {
  const content = parse(tutorial.metadata?.find((m) => m.identifier === identifier)?.content)
  return content && typeof content === 'object' && !Array.isArray(content) ? (content as Record<string, unknown>) : null
}

const text = (value: unknown) => (typeof value === 'string' ? plainText(value).replace(/\s+/g, ' ').trim() : '')

/** "1:02:05" / "12:34" / 754 → seconds. */
export function secondsFromTimestamp(value: unknown): number {
  if (typeof value === 'number') return value
  if (typeof value !== 'string' || !value) return 0
  const parts = value.split(':').map((p) => parseInt(p, 10))
  return parts.some(Number.isNaN) ? 0 : parts.reduce((total, part) => total * 60 + part, 0)
}

export function formatTimestamp(seconds: number): string {
  const value = Math.max(0, Math.floor(seconds))
  const h = Math.floor(value / 3600)
  const m = Math.floor((value % 3600) / 60)
  const s = String(value % 60).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`
}

export function chaptersOf(tutorial: WithMetadata): Chapter[] {
  const raw = contentOf(tutorial, 'video_chapters')?.chapters
  if (!Array.isArray(raw)) return []
  return raw.flatMap((c, i) => {
    const name = text((c as { name?: unknown })?.name)
    return name ? [{ name, startOffset: secondsFromTimestamp((c as { startOffset?: unknown }).startOffset), position: i + 1 }] : []
  })
}

export function faqOf(tutorial: WithMetadata): FaqItem[] {
  const raw = contentOf(tutorial, 'ai_faq')?.questions
  if (!Array.isArray(raw)) return []
  return raw.flatMap((item) => {
    const question = text((item as { question?: unknown })?.question)
    const answer = text((item as { answer?: unknown })?.answer)
    return question && answer ? [{ question, answer }] : []
  })
}

export function learnItemsOf(tutorial: WithMetadata): string[] {
  const raw = contentOf(tutorial, 'what_youll_learn')?.items
  if (!Array.isArray(raw)) return []
  return raw.map((item) => text(typeof item === 'string' ? item : ((item as { title?: unknown; name?: unknown })?.title ?? (item as { name?: unknown })?.name))).filter(Boolean)
}

export const summaryOf = (tutorial: WithMetadata) => text(contentOf(tutorial, 'ai_summary')?.summary)

export const seoDescriptionOf = (tutorial: WithMetadata) => text(contentOf(tutorial, 'seo_description')?.description)
